import { getTextField } from '@lender/utils';
import _ from 'lodash';
import { array, object } from 'yup';

import {
  DataType,
  EntityAssignmentType,
  LoanTaskFormStatus,
  TaskActionType,
} from 'constants/taskCategories';
import { TaskAssignmentType } from 'constants/tasks';
import { getConditionalObject } from 'utils/data';
import { findPriorToOption, getPriorToRequestFields } from 'utils/priorTo';
import { replaceValuesInsideCurlyBrackets } from 'utils/string';
import { checkIsBorrowerPairValidated, getBorrowerPairError } from 'utils/taskEntity';
import { getTextFieldBuilder } from 'utils/validation';

const CONDITION_MESSAGE_MAX_LENGTH = 1000;

// The inline helper text alone left an incompatible pair submittable, so the same rule gates the
// schema. The comparison needs the option OBJECTS, and every field is cast to a string before a
// test runs, so it reads them from the live form useForm passes as validation context rather than
// from the values the schema happened to be built with — one caller rebuilds only on a structural
// key, so a build-time answer would never see the selections there. Reported onto the entity
// field, where the message already renders.
const withBorrowerPairValidation = schema =>
  schema.test({
    name: 'borrowerApplicationGroup',
    test() {
      const { entity, assignee } = _.get(this.options.context, this.path) ?? {};
      const pairError = getBorrowerPairError(entity, assignee);

      return pairError
        ? this.createError({ path: `${this.path}.entity`, message: pairError })
        : true;
    },
  });

const getTaskTypeSchema = ({ conditionTaskTypes = {}, taskTypeDynamicFieldOptions }) =>
  object().shape(
    Object.values(conditionTaskTypes).reduce((acc, taskType) => {
      const isFormTaskType = taskType?.dataType === DataType.FORMS;
      const isBorrowerPairValidated = checkIsBorrowerPairValidated(taskType);

      const taskTypeFields = object().shape({
        assignee: getTextField(),
        description: getTextFieldBuilder(taskTypeDynamicFieldOptions, {
          singleOption: false,
        }).nullable(),
        ...([
          EntityAssignmentType.ASSET,
          EntityAssignmentType.INCOME,
          EntityAssignmentType.BORROWER,
        ].includes(taskType?.entityAssignmentType) && {
          entity: getTextField(),
        }),
        ...(isFormTaskType && {
          formVariant: getTextField(),
          formId: getTextField(),
          state: getTextField({ isRequired: false }),
        }),
      });

      acc[taskType.taskTypeId] = isBorrowerPairValidated
        ? withBorrowerPairValidation(taskTypeFields)
        : taskTypeFields;

      return acc;
    }, {}),
  );

export const getSchema = ({ conditionTaskTypes = {}, taskTypeDynamicFieldOptions }) => {
  // overall structure has to be {[conditionId]: {conditionTaskTypes: {[taskTypeId]: {...taskTypeFields}}}}
  const conditionTaskTypesValidationSchema = Object.values(conditionTaskTypes).reduce(
    (acc, condition) => {
      acc[condition.conditionId] = object().shape({
        message: getTextField().max(CONDITION_MESSAGE_MAX_LENGTH),
        priorToType: getTextField(),
        borrowerMessage: getTextField({ isRequired: false })
          .max(CONDITION_MESSAGE_MAX_LENGTH)
          .nullable(),
        subCompanyMessage: getTextField({ isRequired: false })
          .max(CONDITION_MESSAGE_MAX_LENGTH)
          .nullable(),
        conditionTaskTypes: getTaskTypeSchema({
          conditionTaskTypes: condition.conditionTaskTypes,
          taskTypeDynamicFieldOptions,
        }),
      });

      return acc;
    },
    {},
  );

  return object().shape({
    conditions: array().of(getTextField()),
    conditionSets: array().of(getTextField()),
    conditionTaskTypes: object().shape(conditionTaskTypesValidationSchema),
  });
};

export const getBrokerConditionSchema = ({
  conditionTaskTypes = {},
  taskTypeDynamicFieldOptions,
  isInternal = false,
}) =>
  object().shape({
    internalText: getTextField(),
    borrowerInternalText: getTextField({ isRequired: false }),
    priorToType: getTextField(),
    taskTypeIds: array(),
    ...(isInternal && { slaGroupId: getTextField() }),
    conditionTaskTypes: getTaskTypeSchema({
      conditionTaskTypes,
      taskTypeDynamicFieldOptions,
    }),
  });

export const getConditionTasksSchema = ({ conditionTaskTypes = {}, taskTypeDynamicFieldOptions }) =>
  object().shape({
    taskTypeIds: array(),
    conditionTaskTypes: getTaskTypeSchema({
      conditionTaskTypes,
      taskTypeDynamicFieldOptions,
    }),
  });

const getFlatConditionIdsList = ({ conditions, conditionSets }) => {
  const conditionIds = new Set();
  conditions.forEach(({ id }) => {
    conditionIds.add(id);
  });
  conditionSets.forEach(({ links }) => {
    links.forEach(({ conditionId }) => {
      conditionIds.add(conditionId);
    });
  });

  return Array.from(conditionIds);
};

export const getConditionsTaskType = ({
  form,
  detailedConditions,
  taskTypeDynamicFieldOptions,
  priorToOptions,
}) => {
  const { conditions, conditionSets, conditionTaskTypes } = form;

  const flatConditions = getFlatConditionIdsList({ conditions, conditionSets }).map(
    conditionId => detailedConditions[conditionId],
  );

  return flatConditions.reduce((acc, condition) => {
    const formCondition = conditionTaskTypes[condition.id];
    const removedTaskTypeIds = formCondition?.removedTaskTypeIds ?? [];

    acc[condition.id] = {
      ...condition,
      conditionId: condition.id,
      priorToType: findPriorToOption({
        options: priorToOptions,
        priorToTypeId: condition.priorToTypeId,
        priorToType: condition.priorToType,
      }),
      ...formCondition,
      conditionTaskTypes: { ...formCondition?.conditionTaskTypes },
    };
    condition.taskTypes
      .filter(({ taskTypeId }) => !removedTaskTypeIds.includes(taskTypeId))
      .forEach(taskType => {
        acc[condition.id].conditionTaskTypes[taskType.taskTypeId] = {
          conditionId: condition.id,
          ...taskType,
          description: replaceValuesInsideCurlyBrackets({
            string: taskType.description,
            options: taskTypeDynamicFieldOptions,
            keyToReplace: 'value',
            keyToReplaceWith: 'name',
          }),
          name: replaceValuesInsideCurlyBrackets({
            string: taskType.name,
            options: taskTypeDynamicFieldOptions,
            keyToReplace: 'value',
            keyToReplaceWith: 'name',
          }),
          ...formCondition?.conditionTaskTypes?.[taskType.taskTypeId],
        };
      });

    return acc;
  }, {});
};

export const prepareDataBeforeRequest = ({ form, loanId, taskTypeDynamicFieldOptions }) => {
  const { conditionTaskTypes, conditions, conditionSets } = form;
  const conditionIds = Object.keys(conditionTaskTypes);

  const conditionsWithoutTasks = getFlatConditionIdsList({ conditions, conditionSets }).filter(
    conditionId => !conditionIds.some(cId => conditionId === cId),
  );

  const loanConditions = Object.values(conditionTaskTypes).reduce(
    (
      acc,
      { conditionId, message, conditionTaskTypes, borrowerMessage, subCompanyMessage, priorToType },
    ) => {
      acc.push({
        conditionId,
        message,
        ...getPriorToRequestFields(priorToType),
        borrowerMessage,
        subCompanyMessage: subCompanyMessage || '',
        tasks: Object.values(conditionTaskTypes).map(taskType => ({
          taskTypeId: taskType.taskTypeId,
          taskAssignment: {
            loanId,
            ...(taskType?.taskAssignmentType === TaskAssignmentType.SLA_GROUP
              ? { type: 'SLA', slaGroupId: taskType.assignee?.value }
              : { type: 'Borrower', borrowerId: taskType.assignee?.value }),
          },
          name: replaceValuesInsideCurlyBrackets({
            string: taskType.name,
            options: taskTypeDynamicFieldOptions,
            keyToReplace: 'name',
            keyToReplaceWith: 'value',
          }),
          description: replaceValuesInsideCurlyBrackets({
            string: taskType.description,
            options: taskTypeDynamicFieldOptions,
            keyToReplace: 'name',
            keyToReplaceWith: 'value',
          }),
          actionType: {
            type: taskType.dataType,
            ...getConditionalObject(taskType?.dataType === DataType.FORMS, {
              formBundleId: taskType?.formId?.value,
              isSignatureRequired: taskType?.isSignatureRequired ?? false,
              formStatus: taskType?.formStatus ?? LoanTaskFormStatus.NEW,
              type: TaskActionType.FORM,
            }),
            ...getConditionalObject(taskType?.dataType === DataType.DOCUMENTS, {
              type: TaskActionType.DOCUMENT,
            }),
          },
          relationId:
            taskType?.entity?.incomeId || taskType?.entity?.assetId || taskType.entity?.value,
        })),
      });

      return acc;
    },
    [],
  );

  return {
    loanId,
    loanConditions: [
      ...loanConditions,
      ...conditionsWithoutTasks.map(conditionId => ({ conditionId, tasks: [] })),
    ],
  };
};
